export interface BroadcastHandle<T> {
    /** 열려 있는 채널로 메시지 발신 (best-effort). */
    post: (message: T) => void;
    /** 채널 닫기 (idempotent). */
    close: () => void;
}

const isSupported = (): boolean =>
    typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';

/**
 * BroadcastChannel 저수준 프리미티브.
 * SSR/미지원 환경에서 안전하고, 모든 채널 조작을 best-effort(throw 안 함)로 감싼다.
 * Vue 라이프사이클에 의존하지 않으므로 setup 밖(HttpClient, plugin)에서도 호출 가능.
 */
export const useBroadcastChannel = <T>(name: string) => {
    /** 1회성 발신: 열고 → 쏘고 → 닫기. */
    const postOnce = (message: T): void => {
        if (!isSupported()) return;
        try {
            const bc = new BroadcastChannel(name);
            bc.postMessage(message);
            bc.close();
        } catch {
            /* best-effort */
        }
    };

    /** 장기 채널 열기: 발신·수신을 같은 채널에서. 미지원/실패 시 null. */
    const open = (onMessage?: (data: T) => void): BroadcastHandle<T> | null => {
        if (!isSupported()) return null;
        try {
            const bc = new BroadcastChannel(name);
            if (onMessage) {
                bc.onmessage = (event: MessageEvent<T>) => onMessage(event.data);
            }
            return {
                post: (message: T) => {
                    try {
                        bc.postMessage(message);
                    } catch {
                        /* best-effort */
                    }
                },
                close: () => {
                    try {
                        bc.close();
                    } catch {
                        /* best-effort */
                    }
                },
            };
        } catch {
            return null;
        }
    };

    /** 구독 전용: 열어서 수신만 하고, 해지 함수를 반환. */
    const subscribe = (handler: (data: T) => void): (() => void) => {
        const handle = open(handler);
        return () => handle?.close();
    };

    return { isSupported, postOnce, open, subscribe };
};
